import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect
} from 'react';
import { AccountClient, useQuery } from '@nile/graphql';
import { GET_INVOICE_USAGE_DATA } from '@nile/graphql/src/Query/client_management/invoice';
import {
  AppContext,
  getTranslatedText,
  Loader,
  SuperTable
} from '@nile/components';
import { useIntl, FormattedMessage } from 'react-intl';
import { DateTimeCell } from '@nile/mangifera/src/Components/Ticket';
import { Logo } from '@nile/banyan/src/Containers/FormContainers/MSPConfigurationContainer';
import { Modal, Button, DisplayText } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import envConfig from '@nile/home/src/Config/env';

function formatDecimalHours(value) {
  const numberValue =
    typeof value === 'number' ? value : Number(String(value).trim());
  const absolute = Math.abs(numberValue);
  let hours = Math.floor(absolute);
  let minutes = Math.round((absolute - hours) * 60);
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }
  const hourLabel = hours === 1 ? 'hr' : 'hrs';
  const minuteLabel = minutes === 1 ? 'min' : 'mins';
  if (hours > 0 && minutes > 0)
    return `${hours} ${hourLabel} ${minutes} ${minuteLabel}`;
  if (hours > 0) return `${hours} ${hourLabel}`;
  if (minutes > 0) return `${minutes} ${minuteLabel}`;
  return `0 mins`;
}

function QuantityCell({ cell }) {
  const { value } = cell;
  const { unitType } = cell.row.original;
  if (unitType === 'QUANTITY') {
    return (
      <DisplayText element="div" size="large" weight="SemiBold">
        {`${parseFloat(value)} qty`}
      </DisplayText>
    );
  }

  if (unitType === 'HOURS') {
    return (
      <DisplayText element="div" size="large" weight="SemiBold">
        {formatDecimalHours(value)}
      </DisplayText>
    );
  }
}

const InvoiceUsageReport = ({ usagesData }) => {
  const usages = usagesData?.getInvoiceUsageData || [];

  const intl = useIntl();

  const appContext = useContext(AppContext);
  const { company: companyContext } = appContext;

  const getTableColumnData = useCallback(() => {
    const cols = [
      {
        name: 'billDate',
        label: getTranslatedText(intl, 'invoice.details.list.date'),
        width: 15,
        type: 'DateTime',
        dateType: 'date'
      },
      {
        name: 'description',
        label: getTranslatedText(
          intl,
          'admin.ticketScheduler.attribute.description'
        ),
        width: 55
      },
      {
        name: 'itemName',
        label: getTranslatedText(intl, 'invoice.details.list.itemDetails'),
        width: 15
      },
      {
        name: 'quantity',
        label: getTranslatedText(intl, 'invoice.details.list.quantityOrHours'),
        width: 15,
        type: 'Quantity'
      }
    ];

    return cols.map((c) => {
      return {
        Header: c.label,
        header: c.label,
        accessor: c.name,
        accessorKey: c.name,
        id: c.name,
        // width: c.width,
        isBold: c.name === 'quantity',
        disableSortBy: true,
        disableFilters: true,
        type: c.type,
        dataType: c.dateType
      };
    });
  }, [intl]);

  const columns = useMemo(
    () => [{ Header: 'Name', header: 'name', columns: getTableColumnData() }],
    [getTableColumnData]
  );

  const groupedByContract = usages.reduce((acc, usage) => {
    acc[usage.contractName] = acc[usage.contractName] || [];
    acc[usage.contractName].push(usage);
    return acc;
  }, {});

  function renderTable({ header, data }) {
    return (
      <>
        <div className="usage-report-plan">{header}</div>
        <SuperTable
          key={header}
          columns={columns}
          refreshTable={() => {}}
          context={{ records: [] }}
          emptyState={{
            nodataImage: `${envConfig.imageRoot}svg/not-found.svg`,
            heading: getTranslatedText(intl, 'invoice.list.noItems'),
            description: ''
          }}
          listInfo={{}}
          // recordCount={listInfo.totalCount}
          activePage={0}
          data={data}
          isLoading={false}
          // checkRowHighlight={checkRowHighlight}
          defaultColumnCells={{
            Quantity: QuantityCell,
            DateTime: DateTimeCell
          }}
          noBulkSelect
          fullHeight
          dark
          alignStart
          rowCount={10000}
          pagination={false}
          pages={0}
          recordCount={data.length}
        />
      </>
    );
  }

  const totalHoursDecimal = usages
    .filter((r) => r?.unitType === 'HOURS')
    .map((r) =>
      typeof r.quantity === 'number' ? r.quantity : Number(r.quantity ?? '')
    )
    .reduce((acc, n) => acc + n, 0);

  const hasHoursRows = usages.some((r) => r?.unitType === 'HOURS');
  return (
    <div className="overflow-y-auto">
      {/* <Logo image={{ logoFileName: companyContext?.logoFileName }} /> */}
      <div className="">
        {Object.entries(groupedByContract).map(([header, items]) => {
          return renderTable({
            header,
            // columns: getTableColumnData(),
            data: items
          });
        })}
        {hasHoursRows && (
          <div className="usage-report-footer">
            {getTranslatedText(intl, 'invoice.usageReport.totalHours')}{' '}
            <b>{formatDecimalHours(totalHoursDecimal)}</b>
          </div>
        )}
      </div>
    </div>
  );
};

export const InvoiceUsageReportPreviewButton = (props) => {
  const [open, setOpen] = useState(false);

  const { data: usagesData, loading } = useQuery(GET_INVOICE_USAGE_DATA, {
    client: AccountClient,
    fetchPolicy: 'no-cache',
    variables: {
      invoiceId: props.invoiceId
    }
  });

  useEffect(() => {
    props.onDataChange(usagesData?.getInvoiceUsageData?.length > 0);
  }, [usagesData?.getInvoiceUsageData?.length]);

  if (loading) {
    return <Loader />;
  }

  if (!usagesData?.getInvoiceUsageData?.length) {
    return null;
  }

  return (
    <>
      <div className="mr10">
        <Button
          onClick={() => setOpen(true)}
          icon={<SVGIcon name="EyeVisible" />}
        >
          <FormattedMessage id="invoice.usageReport.preview" />
        </Button>
      </div>
      {open && (
        <Modal
          large
          open
          onClose={() => setOpen(false)}
          title={
            <DisplayText element="div" size="extraLarge" weight="SemiBold">
              <FormattedMessage id="invoice.usageReport.header" />
            </DisplayText>
          }
        >
          <InvoiceUsageReport {...props} usagesData={usagesData} />
        </Modal>
      )}
    </>
  );
};

export default InvoiceUsageReport;
